import { gql, useMutation } from '@apollo/client';
import type { BugReport, Chat, Message, MessageType } from '@shady-pines/db';
import type { Uuid } from '@shady-pines/types';
import type { MutationResponse } from '../types';
import { useSupabase } from '../use-supabase';

const CREATE_MESSAGE = gql`
  mutation CreateMessage(
    $body: String!
    $messageType: String!
    $messagerUserId: UUID!
    $replyToMessageId: UUID
    $bugReportId: UUID
    $chatId: UUID
  ) {
    insertIntomessaging_messagesCollection(
      objects: [
        {
          body: $body
          message_type: $messageType
          messager_user_id: $messagerUserId
          reply_to_message_id: $replyToMessageId
          bug_report_id: $bugReportId
          chat_id: $chatId
        }
      ]
    ) {
      records {
        __typename
        id
        body
        message_type
        created_at
        messager_user_id
        reply_to_message_id
        bug_report_id
        chat_id
      }
    }
  }
`;

export const useCreateMessage = () => {
  const supabase = useSupabase();
  const [createMessage, { loading: createMessageLoading }] =
    useMutation<MutationResponse<'insertIntomessaging_messagesCollection', Message>>(
      CREATE_MESSAGE,
    );

  const create = async (input: {
    body: string;
    messageType: MessageType;
    replyToMessageId?: Uuid<Message>;
    bugReportId?: Uuid<BugReport>;
    chatId?: Uuid<Chat>;
  }) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user?.id) {
      throw new Error('User must be authenticated to create messages');
    }

    const messageResult = await createMessage({
      variables: {
        body: input.body,
        messageType: input.messageType,
        messagerUserId: user.id,
        replyToMessageId: input.replyToMessageId,
        bugReportId: input.bugReportId,
        chatId: input.chatId,
      },
    });

    return messageResult;
  };

  return {
    createMessage: create,
    loading: createMessageLoading,
  };
};
